import { useState } from "react"
import styled from "styled-components"

import { Dialog, Text } from "@minuthq/meatball-ui-react"
import { FormikProps, useFormik } from "formik"

import { TaskForm } from "src/components/TaskManagement/TaskForm"
import { usePostTaskEdited } from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { TMaybeHome } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePatchTask } from "src/data/taskManagement/taskManagementQueries"
import {
  TTask,
  TTaskPostBody,
} from "src/data/taskManagement/taskManagementTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { DiscardChangesDialog } from "src/ui/Dialog/DiscardChangesDialog"
import { DropdownSelect } from "src/ui/DropdownSelect/DropdownSelect"

const EDIT_TASK_FORM_ID = "edit_task_form"

export function EditTaskDialog({
  task,
  homes,
  onClose,
}: {
  task: TTask
  homes: TMaybeHome[]
  onClose: () => void
}) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const [showConfirm, setShowConfirm] = useState(false)

  const patchTask = usePatchTask()
  const postTaskEdited = usePostTaskEdited()

  const form = useFormik<TTaskPostBody>({
    initialValues: {
      title: task.title,
      home_id: task.home_id,
      category: task.category,
      description: task.description,
      status: task.status,
      assigned_to: {
        assignee_id: task.assigned_to.assignee_id ?? "",
        type: task.assigned_to.type,
      },
      time_window: task.time_window,
    },
    onSubmit: (values) => {
      patchTask.mutate(
        {
          path: { organization_id: orgId, task_id: task.task_id },
          body: { ...values },
        },
        {
          onSuccess: () => {
            onClose()

            postTaskEdited.mutate({
              task_type: values.category,
            })
          },
        }
      )
    },
  })

  function handleDiscardConfirmed() {
    setShowConfirm(false)
    onClose()
  }

  function handleClose() {
    if (form.dirty) {
      setShowConfirm(true)
    } else {
      onClose()
    }
  }

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      title={<Title form={form} />}
      confirmLabel={t(langKeys.save)}
      formId={EDIT_TASK_FORM_ID}
      closeLabel={t(langKeys.cancel)}
      error={patchTask.isError && t(langKeys.failed_general_error_title)}
      loading={patchTask.isLoading}
    >
      {showConfirm && (
        <DiscardChangesDialog
          open={showConfirm}
          onClose={() => {
            setShowConfirm(false)
          }}
          onConfirm={handleDiscardConfirmed}
        />
      )}
      <form id={EDIT_TASK_FORM_ID} onSubmit={form.handleSubmit}>
        <TaskForm form={form} homes={homes} />
      </form>
    </Dialog>
  )
}

function Title({ form }: { form: FormikProps<TTaskPostBody> }) {
  const { t, langKeys } = useTranslate()

  const options: { label: string; value: TTask["status"] }[] = [
    { label: t(langKeys.task_management_task_status_todo), value: "todo" },
    { label: t(langKeys.task_management_task_status_done), value: "done" },
  ]

  const label =
    options.find((o) => o.value === form.values.status)?.label ??
    t(langKeys.task_management_task_status_title)

  return (
    <TitleBox>
      <Text variant="heading2">{t(langKeys.task_management_edit_task)}</Text>
      <DropdownSelect
        options={options}
        selectedValue={form.values.status}
        onChange={({ option }) => {
          form.setFieldValue("status", option.value)
        }}
        label={label}
      />
    </TitleBox>
  )
}

const TitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
